import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { AppError, toAppError } from './errors';

/** Requests that failed because of the network are worth retrying; 4xx are not. */
function shouldRetry(failureCount: number, error: unknown): boolean {
  const appError = toAppError(error);
  if (appError.isClientError) return false;
  return failureCount < 2;
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Dashboard data is refreshed by WebSocket events, so a short staleTime
      // is enough to dedupe the burst of requests a page mount produces.
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      retry: shouldRetry,
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
      // Keeps the previous page visible while the next one loads, which stops
      // paginated tables from collapsing to a skeleton on every page change.
      placeholderData: <T,>(previous: T): T => previous,
    },
    mutations: {
      retry: false,
    },
  },

  /**
   * Global error surfacing. Queries only toast on background refetch failures —
   * the first load is rendered as an inline error state by the page itself, and
   * showing both would double-report the same problem.
   */
  queryCache: new QueryCache({
    onError: (error, query) => {
      const appError = toAppError(error);
      // 401 is handled by the session-expiry flow; no toast for it.
      if (appError.status === 401) return;
      if (query.state.data !== undefined) {
        toast.error(appError.message, { id: `query-${query.queryHash}` });
      }
    },
  }),

  mutationCache: new MutationCache({
    onError: (error, _variables, _context, mutation) => {
      // Mutations opting out (`meta.silentError`) handle their own messaging,
      // typically to map field errors onto a form.
      if (mutation.meta?.silentError === true) return;
      const appError = toAppError(error);
      if (appError.status === 401) return;
      // 422 / 400 with field errors belong next to the inputs, not in a toast.
      if ((appError.status === 422 || appError.status === 400) && Object.keys(appError.fieldErrors).length > 0) {
        return;
      }
      toast.error(appError.message);
    },
    onSuccess: (_data, _variables, _context, mutation) => {
      const message = mutation.meta?.successMessage;
      if (typeof message === 'string') toast.success(message);
    },
  }),
});

/** Typed `meta` so `successMessage` / `silentError` are not stringly-typed. */
declare module '@tanstack/react-query' {
  interface Register {
    mutationMeta: {
      successMessage?: string;
      silentError?: boolean;
    };
    queryMeta: {
      /** Human name used in inline error states. */
      resourceName?: string;
    };
  }
}

export { AppError };
